import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { useAttachmentUrls } from '@/hooks/useAttachmentUrls';
import { cn } from '@/lib/utils';
import { isPreviousMessageFromSameAuthorSelector } from '@/state/session';
import { Audio } from './Audio';
import { File } from './File';
import { Image } from './Image';
import { Text } from './Text';
import { Video } from './Video';
const isMediaType = (type) => {
    return ['image', 'video', 'audio', 'file'].includes(type);
};
/**
 * Renders a media attachment based on its type
 */
const MediaRenderer = ({ attachment }) => {
    switch (attachment.type) {
        case 'video':
            return _jsx(Video, { url: attachment.url, name: attachment.title });
        case 'image':
            return _jsx(Image, { url: attachment.url, name: attachment.title });
        case 'file':
            return _jsx(File, { url: attachment.url, name: attachment.title });
        case 'audio':
            return _jsx(Audio, { url: attachment.url });
        default:
            return null;
    }
};
/**
 * Renders an agent's response event, handling both new and legacy formats
 */
export const AgentResponseEvent = ({ event }) => {
    var _a, _b;
    const attachments = useMemo(() => { var _a; return ((_a = event.body) === null || _a === void 0 ? void 0 : _a.attachments) || []; }, [(_a = event.body) === null || _a === void 0 ? void 0 : _a.attachments]);
    const isPreviousMessageFromSameAuthor = useRecoilValue(isPreviousMessageFromSameAuthorSelector(event.event_id));
    const attachmentUrls = useAttachmentUrls(attachments);
    const normalizedAttachments = useMemo(() => attachments
        .map((attachment) => {
        if (!attachment.id)
            return null;
        const url = attachmentUrls[attachment.id];
        if (!url || !attachment.title || !isMediaType(attachment.type))
            return null;
        return {
            id: attachment.id,
            type: attachment.type,
            url,
            title: attachment.title,
        };
    })
        .filter((att) => att !== null), [attachments, attachmentUrls]);
    return (_jsxs("div", { className: "mt-3", "data-event-type": "agent_message", children: [!isPreviousMessageFromSameAuthor && (_jsxs("div", { className: cn('text-foreground bg-surface-button-secondary mt-6 mb-1 flex max-w-md gap-1 rounded-full px-2 py-1 text-center text-sm leading-4 font-semibold'), children: [_jsx("img", { src: event.author_avatar, alt: event.author_name, className: "h-4 w-4 rounded-full" }), _jsx("p", { className: "max-w-full truncate", children: event.author_name })] })), ((_b = event.body) === null || _b === void 0 ? void 0 : _b.text) && _jsx(Text, { event: event }), normalizedAttachments.map((attachment) => (_jsx("div", { className: "mt-2", children: _jsx(MediaRenderer, { attachment: attachment }, attachment.id) }, attachment.id)))] }));
};
